import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Package, Loader2, AlertCircle, Search, ExternalLink, Printer, Eye, X, FileText
} from 'lucide-react';
import axios from 'axios';
import QRCode from 'qrcode';
import { getSectionPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const InventoryDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const perms = getSectionPerms('inventory');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [itemSortOrder, setItemSortOrder] = useState('oldest');
  const [invoiceModal, setInvoiceModal] = useState(null); // { item_id, invoices, qty_full, qty_sold, qty_remaining }

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${BACKEND_URL}/api/inventory/${productId}`, getAuthHeaders());
        setData(res.data);
      } catch (err) {
        setError('Failed to load inventory details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [productId]);

  const statusColor = (status) => {
    switch (status) {
      case 'in_stock':      return 'bg-green-100 text-green-700';
      case 'part_sold':     return 'bg-orange-100 text-orange-700';
      case 'sold':          return 'bg-red-100 text-red-700';
      case 'returned_to_vendor': return 'bg-rose-100 text-rose-700';
      case 'assigned_to_dc':return 'bg-amber-100 text-amber-700';
      case 'rented':        return 'bg-purple-100 text-purple-700';
      case 'servicing':     return 'bg-yellow-100 text-yellow-700';
      default:              return 'bg-slate-100 text-slate-700';
    }
  };

  const printBarcodes = useCallback(async () => {
    if (!data) return;
    const inStockItems = (data.items || []).filter(i => i.status === 'in_stock');
    if (!inStockItems.length) { alert('No in-stock items to print'); return; }

    const QR_CSS = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: Arial, sans-serif; background: #fff; }
      .grid { display: flex; flex-wrap: wrap; gap: 1mm; padding: 3mm; }
      .qr-card {
        width: 25mm; height: 15mm;
        display: inline-flex; flex-direction: column;
        align-items: center; justify-content: center;
        page-break-inside: avoid;
      }
      .qr-img { width: 11mm; height: 11mm; display: block; }
      .code-text { font-size: 5pt; font-family: monospace; margin-top: 0.5mm; text-align: center; white-space: nowrap; }
      @media print { body { -webkit-print-color-adjust: exact; } }
    `;
    const printWin = window.open('', '_blank', 'width=600,height=400');
    printWin.document.write(`<!DOCTYPE html><html><head><title>QR Codes - ${data.product_name || ''}</title><style>${QR_CSS}</style></head><body><div class="grid">`);

    for (const item of inStockItems) {
      try {
        const dataUrl = await QRCode.toDataURL(item.item_id, { width: 120, margin: 1, errorCorrectionLevel: 'M' });
        const card = printWin.document.createElement('div');
        card.className = 'qr-card';
        card.innerHTML = `<img class="qr-img" src="${dataUrl}" alt="QR"/><div class="code-text">${item.item_id}</div>`;
        printWin.document.querySelector('.grid').appendChild(card);
      } catch { /* skip bad item */ }
    }

    printWin.document.write('</div></body></html>');
    printWin.document.close();
    setTimeout(() => { printWin.print(); }, 400);
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div data-testid="inventory-detail-error">
        <button onClick={() => navigate('/admin/inventory')} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to Inventory
        </button>
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm">
          <AlertCircle className="h-5 w-5" />
          <span>{error || 'Not found'}</span>
        </div>
      </div>
    );
  }

  const inv = data.inventory;
  const items = data.items || [];

  // Collect unique statuses for filter
  const statuses = [...new Set(items.map(i => i.status).filter(Boolean))];

  const filteredItems = [...items].sort((a, b) => {
    const cmp = (a.item_id || '').localeCompare(b.item_id || '');
    return itemSortOrder === 'newest' ? -cmp : cmp;
  }).filter(item => {
    const matchesSearch = !searchQuery ||
      item.item_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sub_rack_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.batch_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Count by status
  const statusCounts = {};
  items.forEach(i => {
    const s = i.status || 'unknown';
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });

  return (
    <div data-testid="inventory-detail-page">
      {/* Back */}
      <button
        onClick={() => navigate('/admin/inventory')}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Inventory
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">{data.product_name}</h1>
          {data.product_code && <p className="text-slate-500 font-mono mt-1">{data.product_code}</p>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={printBarcodes} data-testid="print-barcode-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-800 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-slate-900 transition-colors">
            <Printer className="h-3.5 w-3.5" /> Print Barcodes
          </button>
          <div className="bg-green-100 text-green-700 px-4 py-2 rounded-sm text-sm font-bold">
            {inv.quantity} In Stock
          </div>
          <div className="bg-slate-100 text-slate-700 px-4 py-2 rounded-sm text-sm font-bold">
            {data.total_items} Total Items
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {!perms.hidePrices && (
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Purchase Rate</div>
          <div className="text-lg font-bold text-slate-900">₹{inv.purchase_rate?.toLocaleString() || '-'}</div>
        </div>
        )}
        {!perms.hidePrices && (
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Sale Rate</div>
          <div className="text-lg font-bold text-slate-900">₹{inv.sale_rate?.toLocaleString() || '-'}</div>
        </div>
        )}
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Display Rate</div>
          <div className="text-lg font-bold text-orange-600">₹{inv.display_rate?.toLocaleString() || '-'}</div>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">By Status</div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {Object.entries(statusCounts).map(([s, c]) => (
              <span key={s} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColor(s)}`}>
                {s.replace(/_/g, ' ')}: {c}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6 flex flex-col md:flex-row gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by Item ID, batch, or rack..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="item-search"
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          data-testid="status-filter"
          className="px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none"
        >
          <option value="">All Statuses</option>
          {statuses.map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ').toUpperCase()}</option>
          ))}
        </select>
        {/* Sort toggle */}
        <div className="flex rounded-sm border border-slate-200 overflow-hidden text-xs font-semibold">
          <button onClick={() => setItemSortOrder('oldest')} data-testid="sort-oldest"
            className={`px-3 py-2 transition-colors ${itemSortOrder === 'oldest' ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
            Oldest First
          </button>
          <button onClick={() => setItemSortOrder('newest')} data-testid="sort-newest"
            className={`px-3 py-2 transition-colors border-l border-slate-200 ${itemSortOrder === 'newest' ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
            Newest First
          </button>
        </div>
      </div>

      {/* Items Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Individual Items</h2>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded bg-green-100 text-green-700">In Stock: {statusCounts['in_stock'] || 0}</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded bg-red-100 text-red-700">Sold: {statusCounts['sold'] || 0}</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded bg-amber-100 text-amber-700">Assigned to DC: {statusCounts['assigned_to_dc'] || 0}</span>
            <span className="text-sm text-slate-500">{filteredItems.length} shown</span>
          </div>
        </div>

        {filteredItems.length === 0 ? (
          <div className="text-center py-12" data-testid="no-items">
            <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No items found</p>
          </div>
        ) : (
          <>
            <div className="hidden md:grid gap-4 px-6 py-3 border-b border-slate-200 bg-slate-50 md:grid-cols-[40px_2fr_3fr_2fr_1.5fr_1.5fr_1.5fr_60px]">
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">#</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Item ID</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Company</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Display Price</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Rack</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Batch</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider text-right">View</div>
            </div>

            {filteredItems.map((item, idx) => (
              <div
                key={item.id}
                data-testid={`inv-item-row-${idx}`}
                className="grid grid-cols-1 md:grid-cols-[40px_2fr_3fr_2fr_1.5fr_1.5fr_1.5fr_60px] gap-4 px-6 py-3 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div className="text-slate-400 text-sm">{idx + 1}</div>
                <div>
                  <span className="font-mono font-medium text-slate-900 text-sm">{item.item_id}</span>
                  {item.created_at && (
                    <div className="text-xs text-slate-400 mt-0.5">
                      {new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Status badge — show PART SOLD for part-sold items */}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    item.is_part_sold ? statusColor('part_sold') : statusColor(item.status)
                  }`}>
                    {item.is_part_sold
                      ? `PART SOLD (${item.part_sale_qty_sold}/${item.part_sale_qty_full})`
                      : item.status?.replace(/_/g, ' ').toUpperCase()}
                  </span>

                  {/* Single invoice link (sold, non-part-sale) */}
                  {item.status === 'sold' && !item.is_part_sold && item.sale_invoice_id && (
                    <Link to={`/admin/sale/invoices/${item.sale_invoice_id}`}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid={`link-sale-${idx}`}>
                      {item.sale_invoice_number || 'View Invoice'} <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}

                  {/* View Invoices button for part-sold items with invoice history */}
                  {item.is_part_sold && item.part_sale_invoices?.length > 0 && (
                    <button
                      onClick={() => setInvoiceModal({
                        item_id: item.item_id,
                        invoices: item.part_sale_invoices,
                        qty_full: item.part_sale_qty_full,
                        qty_sold: item.part_sale_qty_sold,
                        qty_remaining: item.part_sale_qty_remaining,
                      })}
                      data-testid={`view-invoices-btn-${idx}`}
                      className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 font-medium bg-orange-50 hover:bg-orange-100 px-2 py-0.5 rounded transition-colors"
                    >
                      <FileText className="h-3 w-3" /> View Invoices ({item.part_sale_invoices.length})
                    </button>
                  )}

                  {item.status === 'assigned_to_dc' && item.dc_id && (
                    <Link to={`/admin/sale/delivery-challan/${item.dc_id}`}
                      className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium" data-testid={`link-dc-${idx}`}>
                      {item.dc_number || 'View DC'} <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </div>
                <div className="text-sm text-slate-700" data-testid={`inv-item-company-${idx}`}>
                  {item.company_name || <span className="text-slate-400">-</span>}
                </div>
                <div>
                  <span className="font-medium text-slate-900">
                    {item.display_rate != null ? `₹${Number(item.display_rate).toLocaleString()}` : '-'}
                  </span>
                </div>
                <div className="text-sm text-slate-600">
                  {item.sub_rack_name || '-'}
                </div>
                <div className="text-xs text-slate-400 font-mono">
                  {item.batch_number || '-'}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => navigate(`/admin/inventory/items/${encodeURIComponent(item.item_id)}`)}
                    data-testid={`view-item-btn-${idx}`}
                    title="View item history"
                    className="p-1.5 text-slate-500 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* View Invoices Modal */}
      {invoiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-lg flex flex-col" style={{ maxHeight: '80vh' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
              <div>
                <h3 className="font-bold text-slate-900 uppercase tracking-tight">Part Sale Invoices</h3>
                <p className="text-xs text-slate-500 font-mono mt-0.5">{invoiceModal.item_id}</p>
              </div>
              <button onClick={() => setInvoiceModal(null)} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Qty summary */}
            <div className="flex items-center gap-4 px-5 py-3 bg-slate-50 border-b border-slate-100 flex-shrink-0 text-xs">
              <span className="text-slate-500">Full qty: <span className="font-bold text-slate-800">{invoiceModal.qty_full}</span></span>
              <span className="text-orange-600">Sold: <span className="font-bold">{invoiceModal.qty_sold}</span></span>
              <span className="text-green-700">Remaining: <span className="font-bold">{invoiceModal.qty_remaining}</span></span>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {invoiceModal.invoices.map((inv, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50">
                  <div>
                    <p className="font-mono text-sm font-semibold text-slate-900">{inv.invoice_number}</p>
                    {inv.customer_name && <p className="text-xs text-slate-500 mt-0.5">{inv.customer_name}</p>}
                    {inv.invoice_date && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(inv.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-orange-700 bg-orange-50 px-2 py-0.5 rounded">
                      Qty: {inv.qty_sold}
                    </span>
                    <Link
                      to={`/admin/sale/invoices/${inv.invoice_id}`}
                      onClick={() => setInvoiceModal(null)}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                      data-testid={`modal-invoice-link-${i}`}
                    >
                      Open <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-shrink-0 px-5 py-3 border-t border-slate-200 bg-white">
              <button onClick={() => setInvoiceModal(null)}
                className="w-full px-4 py-2 border border-slate-200 text-slate-700 rounded-sm text-sm hover:bg-slate-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
